import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Building2, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

type WizardStep = 'org' | 'complete';

export default function OnboardingPage() {
  const { signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('org');

  const handleGoBack = async () => {
    await signOut();
    navigate('/');
  };

  const [country, setCountry] = useState('SE');
  const [timezone, setTimezone] = useState('Europe/Stockholm');

  const handleSetupOrganization = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const orgName = formData.get('orgName') as string;

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Check if org already exists
      const { data: existingOrg } = await supabase
        .from('orgs')
        .select('*')
        .eq('name', orgName)
        .maybeSingle();

      let createdOrgId: string;

      if (existingOrg) {
        createdOrgId = existingOrg.id;
      } else {
        // Generate UUID for the org
        const newOrgId = crypto.randomUUID();
        
        // Create new organization with explicit ID
        const insertResult = await supabase
          .from('orgs')
          .insert({
            id: newOrgId,
            name: orgName,
            country,
            timezone,
            language: 'sv-SE'
          });
        
        if (insertResult.error) throw insertResult.error;

        createdOrgId = newOrgId;
      }

      // Update user profile with org_id (role is handled by handle_new_user trigger)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ org_id: createdOrgId })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // Create default NIS2 standard if it doesn't exist
      const { error: standardError } = await supabase
        .from('standards')
        .upsert({
          key: 'NIS2',
          name: 'Network and Information Security Directive 2',
          enabled: true,
          org_id: createdOrgId
        }, {
          onConflict: 'org_id,key',
          ignoreDuplicates: true
        });

      if (standardError && !standardError.message.includes('duplicate')) {
        console.error('Standard creation warning:', standardError);
      }

      // Invalidate and refetch all queries to get fresh data
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
      await queryClient.refetchQueries({ queryKey: ['profile'] });
      
      // Small delay to ensure queries complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Set completion step briefly
      setCurrentStep('complete');
      
      toast({
        title: "Organization created!",
        description: "Redirecting to dashboard...",
      });
      
      // Then redirect to dashboard
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500);
      
    } catch (error: any) {
      toast({
        title: "Error setting up organization",
        description: error.message || 'Please try again',
        variant: "danger",
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <button 
              onClick={handleGoBack}
              className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
              title="Go back to homepage"
            >
              <Shield className="h-8 w-8 text-white" />
            </button>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome to ComplyManager</h1>
          <p className="text-white/80">
            {currentStep === 'org' && 'Set up your organization to get started'}
            {currentStep === 'complete' && 'Organization created successfully!'}
          </p>
        </div>

        {/* Step 1: Organization Setup */}
        {currentStep === 'org' && (
          <Card className="bg-card/95 dark:bg-card backdrop-blur shadow-elevated border border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Organization Setup
              </CardTitle>
              <CardDescription>
                Create your compliance workspace for NIS2 management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSetupOrganization} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name *</Label>
                  <Input
                    id="orgName"
                    name="orgName"
                    type="text"
                    placeholder="Enter your organization name"
                    required
                    disabled={isLoading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select value={country} onValueChange={setCountry} disabled={isLoading}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SE">Sweden (SE)</SelectItem>
                      <SelectItem value="NO">Norway (NO)</SelectItem>
                      <SelectItem value="DK">Denmark (DK)</SelectItem>
                      <SelectItem value="FI">Finland (FI)</SelectItem>
                      <SelectItem value="DE">Germany (DE)</SelectItem>
                      <SelectItem value="NL">Netherlands (NL)</SelectItem>
                      <SelectItem value="BE">Belgium (BE)</SelectItem>
                      <SelectItem value="FR">France (FR)</SelectItem>
                      <SelectItem value="ES">Spain (ES)</SelectItem>
                      <SelectItem value="IT">Italy (IT)</SelectItem>
                      <SelectItem value="PL">Poland (PL)</SelectItem>
                      <SelectItem value="CZ">Czech Republic (CZ)</SelectItem>
                      <SelectItem value="AT">Austria (AT)</SelectItem>
                      <SelectItem value="CH">Switzerland (CH)</SelectItem>
                      <SelectItem value="UK">United Kingdom (UK)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone} disabled={isLoading}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Europe/Stockholm">Europe/Stockholm (CET)</SelectItem>
                      <SelectItem value="Europe/Oslo">Europe/Oslo (CET)</SelectItem>
                      <SelectItem value="Europe/Copenhagen">Europe/Copenhagen (CET)</SelectItem>
                      <SelectItem value="Europe/Helsinki">Europe/Helsinki (EET)</SelectItem>
                      <SelectItem value="Europe/Berlin">Europe/Berlin (CET)</SelectItem>
                      <SelectItem value="Europe/Amsterdam">Europe/Amsterdam (CET)</SelectItem>
                      <SelectItem value="Europe/Brussels">Europe/Brussels (CET)</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris (CET)</SelectItem>
                      <SelectItem value="Europe/Madrid">Europe/Madrid (CET)</SelectItem>
                      <SelectItem value="Europe/Rome">Europe/Rome (CET)</SelectItem>
                      <SelectItem value="Europe/Warsaw">Europe/Warsaw (CET)</SelectItem>
                      <SelectItem value="Europe/Prague">Europe/Prague (CET)</SelectItem>
                      <SelectItem value="Europe/Vienna">Europe/Vienna (CET)</SelectItem>
                      <SelectItem value="Europe/Zurich">Europe/Zurich (CET)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Get Started'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Complete Step */}
        {currentStep === 'complete' && (
          <Card className="bg-card/95 dark:bg-card backdrop-blur shadow-elevated border border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-success" />
                Setup Complete!
              </CardTitle>
              <CardDescription>
                Your organization has been created successfully
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-success/10 border border-success/20 p-4 rounded-lg">
                  <p className="text-sm text-success">
                    Redirecting you to the dashboard...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
